// neon-tech — c02 Boshlanish Tadbiri (corporate launch).
//
// Paper-invitation preview, dark-paper edition. A champagne-gold foil
// border wraps a deep-charcoal inner card. The focal medallion is a
// concentric ring + diamond cluster — a quiet corporate launch mark
// rather than a hackathon terminal. Eyebrow reads OCHILISH (opening).
// Names use the template's Inter font (uppercase tracked). Footer is
// small-caps date + venue. All colour values flow from LayoutCtx.
import { type LayoutCtx } from "./_shared";

const FOIL =
  "linear-gradient(135deg, #8A6F3A 0%, #B89255 22%, #E5C892 45%, #B89255 65%, #8A6F3A 100%)";

export function NeonTechLayout({ names, date, venue, accent, ink, bg, font }: LayoutCtx) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: font,
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.42em",
            color: accent,
            marginBottom: "5%",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ width: 14, height: 1, background: accent }} />
          OCHILISH
          <span style={{ width: 14, height: 1, background: accent }} />
        </div>

        {/* Corporate launch medallion — concentric ring with diamond cluster */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOIL,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "6%",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg viewBox="0 0 100 100" style={{ width: "88%", height: "88%" }} aria-hidden>
                {/* Concentric champagne rings */}
                <circle cx="50" cy="50" r="44" fill="none" stroke={accent} strokeWidth="0.5" opacity="0.4" />
                <circle cx="50" cy="50" r="36" fill="none" stroke={accent} strokeWidth="0.7" opacity="0.7" />
                <circle cx="50" cy="50" r="28" fill="none" stroke={accent} strokeWidth="0.9" />
                {/* Compass tick marks */}
                {Array.from({ length: 24 }).map((_, i) => {
                  const deg = i * 15;
                  const r1 = 40;
                  const r2 = i % 6 === 0 ? 34 : 37;
                  const rad = (deg - 90) * (Math.PI / 180);
                  return (
                    <line
                      key={`t${i}`}
                      x1={50 + Math.cos(rad) * r1}
                      y1={50 + Math.sin(rad) * r1}
                      x2={50 + Math.cos(rad) * r2}
                      y2={50 + Math.sin(rad) * r2}
                      stroke={accent}
                      strokeWidth={i % 6 === 0 ? 0.9 : 0.4}
                      opacity={i % 6 === 0 ? 0.95 : 0.5}
                    />
                  );
                })}
                {/* Diamond cluster at centre */}
                <g transform="translate(50 50)">
                  <path d="M0 -14 L8 0 L0 14 L-8 0 Z" fill={accent} opacity="0.95" />
                  <path d="M0 -14 L8 0 L0 14 L-8 0 Z" fill="none" stroke={bg} strokeWidth="0.4" />
                  <path d="M0 -14 L0 14 M-8 0 L8 0" stroke={bg} strokeWidth="0.3" opacity="0.6" />
                  {/* Glint */}
                  <circle cx="-2.5" cy="-4" r="1.2" fill={ink} opacity="0.55" />
                </g>
                {/* Cardinal labels — Roman numerals */}
                <text x="50" y="14" textAnchor="middle" fontFamily={font} fontSize="3.4" fontWeight="700" letterSpacing="0.18em" fill={accent}>XII</text>
                <text x="50" y="92" textAnchor="middle" fontFamily={font} fontSize="3.4" fontWeight="700" letterSpacing="0.18em" fill={accent}>VI</text>
              </svg>
            </div>
          </div>
        </div>

        {/* Names — Inter uppercase */}
        <div
          style={{
            fontFamily: font,
            fontWeight: 700,
            fontSize: 28,
            lineHeight: 1.08,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Footer */}
        <div
          style={{
            fontFamily: font,
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          OCHILISH · {date}
        </div>
        <div
          style={{
            fontFamily: font,
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
